import React, { useEffect, useRef, useState } from "react";

import { searchSymbols, tickerOf } from "../lib/symbols";

/**
 * Keyboard-driven symbol search.
 *
 * The embed's own search is locked inside the iframe, so this drives the app's
 * state instead — which is what lets the surrounding workspace (layout, URL,
 * watchlist) stay in sync with what's on screen.
 */
const SymbolSearch = ({ onSelect }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const results = searchSymbols(query);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  // "/" focuses search, the convention in every trading terminal.
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = e.target?.tagName;
      const typing =
        tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;

      if (e.key === "/" && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const choose = (entry) => {
    if (!entry) return;
    onSelect(entry.symbol);
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[highlight]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="search" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        className="search__input"
        placeholder="Search symbol…  /"
        aria-label="Search symbol"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlight(0);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={onKeyDown}
      />

      {isOpen && results.length > 0 && (
        <ul className="search__results" role="listbox">
          {results.map((entry, index) => (
            <li key={entry.symbol}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlight}
                className={`search__result${
                  index === highlight ? " search__result--active" : ""
                }`}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(entry)}
              >
                <span className="search__ticker">
                  {tickerOf(entry.symbol)}
                </span>
                <span className="search__name">{entry.name}</span>
                <span className="search__group">{entry.group}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SymbolSearch;
